import { inject, Injectable } from '@angular/core';
import { EncryptedStorageService } from '../encryption/encrypted-storage.service';
import { STORAGE_KEYS } from '../encryption/storage.types';
import { EnrichedRecentProject, RecentProject } from './recent-project.interface';
import { OCP_PROJECT_TYPE } from '../../constants';

const MAX_RECENT_PROJECTS = 4;

/**
 * Cached enriched data for a project (API-fetched data that doesn't change often)
 */
interface CachedProjectData {
  thumbnailUrl?: string;
  modelsCount?: number;
  tasksCount?: number;
}

@Injectable({ providedIn: 'root' })
export class RecentProjectsService {
  private readonly encryptedStorage = inject(EncryptedStorageService);

  private isPortalRecentProject(project: RecentProject): boolean {
    return project.projectType === OCP_PROJECT_TYPE;
  }

  /**
   * In-memory cache for enriched project data.
   * Key: projectId, Value: cached API data (thumbnail, modelsCount)
   * This cache persists for the app session to avoid redundant API calls.
   */
  private readonly enrichedDataCache = new Map<string, CachedProjectData>();

  /**
   * Add a project to the recent projects list.
   * The project is added at the beginning of the list.
   * If the project already exists, it is moved to the beginning.
   * The list is limited to MAX_RECENT_PROJECTS items.
   *
   * @param projectId - The unique identifier of the project
   * @param projectName - The display name of the project
   * @param teamId - The unique identifier of the team
   * @param teamName - The display name of the team
   * @param teamSlug - The slug of the team (required for API calls)
   * @param enrichedData - Optional enriched data to cache (thumbnailUrl, modelsCount)
   */
  addRecentProject(
    projectId: string,
    projectName: string,
    teamId: string,
    teamName: string,
    teamSlug: string,
    enrichedData?: CachedProjectData
  ): void {
    if (!this.encryptedStorage.isInitialized()) {
      return;
    }

    const recentProjects = this.loadRecentProjects();

    // Remove existing entry for the same project (if any)
    const filteredProjects = recentProjects.filter(p => p.projectId !== projectId);

    // Create new entry
    const newEntry: RecentProject = {
      projectName,
      teamName,
      projectId,
      teamId,
      teamSlug,
      projectType: OCP_PROJECT_TYPE,
      selectedAt: new Date().toISOString(),
    };

    // Add to the beginning and limit to max items
    const updatedProjects = [newEntry, ...filteredProjects].slice(0, MAX_RECENT_PROJECTS);

    this.encryptedStorage.setItemAndSave(STORAGE_KEYS.RECENT_PROJECTS, updatedProjects);

    // Update cache if enriched data is provided
    if (enrichedData) {
      this.setCachedEnrichedData(projectId, enrichedData);
    }
  }

  updateRecentProjectName(projectId: string, projectName: string): void {
    if (!this.encryptedStorage.isInitialized()) {
      return;
    }

    const recentProjects = this.loadRecentProjects();
    let changed = false;

    const updatedProjects = recentProjects.map(project => {
      if (project.projectId !== projectId || project.projectName === projectName) {
        return project;
      }

      changed = true;
      return { ...project, projectName };
    });

    if (changed) {
      this.encryptedStorage.setItemAndSave(STORAGE_KEYS.RECENT_PROJECTS, updatedProjects);
    }
  }

  updateRecentProjectThumbnail(projectId: string, thumbnailUrl: string | undefined): void {
    const existing = this.enrichedDataCache.get(projectId);

    this.enrichedDataCache.set(projectId, {
      ...existing,
      thumbnailUrl,
    });
  }

  /**
   * Load the list of recent projects from encrypted storage.
   * @returns Array of recent projects ordered from newest to oldest, or empty array if none exist.
   */
  loadRecentProjects(): RecentProject[] {
    if (!this.encryptedStorage.isInitialized()) {
      return [];
    }
    const storedProjects =
      this.encryptedStorage.getItem<RecentProject[]>(STORAGE_KEYS.RECENT_PROJECTS) ?? [];

    return storedProjects.filter(project => this.isPortalRecentProject(project));
  }

  /**
   * Load enriched recent projects, merging stored data with cached enriched data.
   * @returns Array of enriched recent projects with cached thumbnailUrl and modelsCount.
   */
  loadEnrichedRecentProjects(): EnrichedRecentProject[] {
    const storedProjects = this.loadRecentProjects();

    return storedProjects.map(project => {
      const cached = this.getCachedEnrichedData(project.projectId);
      return {
        ...project,
        thumbnailUrl: cached?.thumbnailUrl,
        modelsCount: cached?.modelsCount,
        tasksCount: cached?.tasksCount,
      };
    });
  }

  /**
   * Get cached enriched data for a project.
   * @param projectId - The project ID to look up
   * @returns Cached data if available, undefined otherwise
   */
  getCachedEnrichedData(projectId: string): CachedProjectData | undefined {
    return this.enrichedDataCache.get(projectId);
  }

  /**
   * Cache enriched data for a project.
   * @param projectId - The project ID
   * @param data - The enriched data to cache
   */
  setCachedEnrichedData(projectId: string, data: CachedProjectData): void {
    this.enrichedDataCache.set(projectId, data);
  }

  /**
   * Check if enriched data is cached for a project.
   * @param projectId - The project ID to check
   * @returns True if cached data exists
   */
  hasCachedEnrichedData(projectId: string): boolean {
    return this.enrichedDataCache.has(projectId);
  }

  /**
   * Get project IDs that are missing from the cache.
   * @param projectIds - Array of project IDs to check
   * @returns Array of project IDs that don't have cached data
   */
  getUncachedProjectIds(projectIds: string[]): string[] {
    return projectIds.filter(id => !this.enrichedDataCache.has(id));
  }

  /**
   * Update the cached model count for a specific project.
   * Used when models are added or deleted to keep the cache in sync.
   * @param projectId - The project ID to update
   * @param modelsCount - The new model count
   */
  updateCachedModelsCount(projectId: string, modelsCount: number): void {
    const existing = this.enrichedDataCache.get(projectId);
    if (existing) {
      this.enrichedDataCache.set(projectId, { ...existing, modelsCount });
    }
  }

  /**
   * Clear all recent projects from storage and cache.
   */
  clearRecentProjects(): void {
    if (!this.encryptedStorage.isInitialized()) {
      return;
    }
    this.encryptedStorage.removeItemAndSave(STORAGE_KEYS.RECENT_PROJECTS);
    this.enrichedDataCache.clear();
  }
}
